import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "./client";
import { queryKeys } from "./keys";
import type {
  CreatePlantProfileInput,
  ListResponse,
  PlantProfile,
  PlantProfileSuggestion,
  PlantProfileSuggestionInput,
  UpdatePlantProfileInput,
} from "./types";

const base = "/v1/plant-profiles";

export function usePlantProfiles() {
  return useQuery({
    queryKey: queryKeys.plantProfiles.list(),
    queryFn: ({ signal }) =>
      api.get<ListResponse<PlantProfile>>(base, undefined, signal),
  });
}

export function usePlantProfile(id: string | undefined) {
  return useQuery({
    queryKey: id
      ? queryKeys.plantProfiles.detail(id)
      : queryKeys.plantProfiles.all,
    queryFn: ({ signal }) =>
      api.get<PlantProfile>(`${base}/${id}`, undefined, signal),
    enabled: !!id,
  });
}

export function useCreatePlantProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePlantProfileInput) =>
      api.post<PlantProfile>(base, input),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: queryKeys.plantProfiles.all });
      qc.setQueryData(queryKeys.plantProfiles.detail(created.id), created);
    },
  });
}

export function useUpdatePlantProfile(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePlantProfileInput) =>
      api.patch<PlantProfile>(`${base}/${id}`, input),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.plantProfiles.detail(id), updated);
      qc.invalidateQueries({ queryKey: queryKeys.plantProfiles.all });
    },
  });
}

export function useArchivePlantProfile(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string | undefined) =>
      api.delete<void>(`${base}/${id}`, { reason: reason ?? "" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.plantProfiles.all });
    },
  });
}

// useSuggestPlantProfile asks the server's AI lookup for target specs given a
// name/species. Display-only — the caller pre-fills the form; nothing persists.
export function useSuggestPlantProfile() {
  return useMutation({
    mutationFn: (input: PlantProfileSuggestionInput) =>
      api.post<PlantProfileSuggestion>(`${base}/ai/suggest`, input),
  });
}
